import React from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { agencyEnablementAPI, levelCapAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

/**
 * DisableCarrierDialog — confirms removal of a carrier enablement (and any
 * associated level cap). Owns its own submission state and API calls.
 */
export const DisableCarrierDialog = ({ open, onOpenChange, enablement, onRemoved }) => {
  const [pending, setPending] = React.useState(false);

  const handleConfirm = async () => {
    if (!enablement) return;
    setPending(true);
    try {
      await agencyEnablementAPI.delete(enablement.enablement_id);
      if (enablement.level_cap?.cap_id) {
        await levelCapAPI.delete(enablement.level_cap.cap_id);
      }
      toast.success('Carrier removed from agency');
      onOpenChange(false);
      onRemoved?.();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to remove carrier'));
    } finally {
      setPending(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="disable-carrier-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Remove Carrier</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to remove <strong>{enablement?.carrier_name}</strong> from this agency?
            <span className="block mt-2 text-amber-600">
              This will also delete the associated level cap. The agency will no longer be able to request contracts for this carrier.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={pending}
            className="bg-red-500 hover:bg-red-600"
            data-testid="confirm-disable-carrier"
          >
            {pending ? 'Removing…' : 'Remove Carrier'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DisableCarrierDialog;
